import {
  Component,
  inject,
  signal,
  computed,
  ElementRef,
  ViewChild,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  DASHBOARD_SEARCH_ITEMS,
  DashboardSearchItem,
} from '../../../core/constants/globalSearch.const';
import { DashboardSearchService } from '../../../services/dashboard-search.service';
import { getUserRoleFromToken } from '../../../core/guards/auth-role.helper';
import { environment } from '../../../../environments/environment';
import { GlobalSearchApiService } from '../../../services/global-search-api.service';
import {
  GlobalSearchQuoteResult,
  GlobalSearchUserResult,
} from '../../../models/global-search.model';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, catchError, takeUntil } from 'rxjs';

@Component({
  selector: 'quote-hub-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent implements OnDestroy {
  @ViewChild('searchWrapper') searchWrapper!: ElementRef<HTMLDivElement>;

  selectedItem = signal<DashboardSearchItem | null>(null);
  private router = inject(Router);
  private searchService = inject(DashboardSearchService);
  private globalSearchApi = inject(GlobalSearchApiService);

  // ── Signals ──────────────────────────────────────────────────────────────
  readonly searchQuery = signal('');
  readonly showDropdown = signal(false);
  readonly isExpanded = signal(false);

  /** API results */
  readonly apiQuotes = signal<GlobalSearchQuoteResult[]>([]);
  readonly apiUsers = signal<GlobalSearchUserResult[]>([]);
  readonly apiLoading = signal(false);

  /** RxJS subject for debounced API calls */
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.searchInput$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < 2) {
            this.apiQuotes.set([]);
            this.apiUsers.set([]);
            this.apiLoading.set(false);
            return of(null);
          }
          this.apiLoading.set(true);
          return this.globalSearchApi.globalSearch(term).pipe(
            catchError(() => {
              this.apiLoading.set(false);
              return of(null);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        if (res) {
          this.apiQuotes.set(res.data?.quotes ?? []);
          this.apiUsers.set(res.data?.users ?? []);
        }
        this.apiLoading.set(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Derived: filter DASHBOARD_SEARCH_ITEMS by query */
  readonly filteredSuggestions = computed<DashboardSearchItem[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    if (!q) return [];

    const role = this.getRole();

    return DASHBOARD_SEARCH_ITEMS.filter((item) => {
      const roleAllowed = item.roles.length === 0 || item.roles.includes(role);
      if (!roleAllowed) return false;

      return (
        item.title.toLowerCase().includes(q) ||
        item.keywords.some((k) => k.toLowerCase().includes(q))
      );
    });
  });

  /** True when any API or local result exists */
  readonly hasAnyResult = computed(() =>
    this.filteredSuggestions().length > 0 ||
    this.apiQuotes().length > 0 ||
    this.apiUsers().length > 0
  );

  /** True when the service has an active filter */
  readonly hasActiveFilter = this.searchService.hasActiveFilter;

  // ── Close on outside click ───────────────────────────────────────────────
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.searchWrapper &&
      !this.searchWrapper.nativeElement.contains(event.target as Node)
    ) {
      this.closeDropdown();
    }
  }

  getRole(): string {
    const token = localStorage.getItem(environment.storageKey) ?? '';
    return getUserRoleFromToken(token);
  }

  // ── Status helpers ────────────────────────────────────────────────────────
  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'badge-pending',
      APPROVED: 'badge-approved',
      IN_PROGRESS: 'badge-inprogress',
      REJECTED: 'badge-rejected',
      COMPLETED: 'badge-completed',
      CHANGE_REQUESTED: 'badge-change',
    };
    return map[status?.toUpperCase()] ?? 'badge-default';
  }

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      IN_PROGRESS: 'In Progress',
      REJECTED: 'Rejected',
      COMPLETED: 'Completed',
      CHANGE_REQUESTED: 'Change Requested',
    };
    return map[status?.toUpperCase()] ?? status;
  }

  // ── Template handlers ────────────────────────────────────────────────────
  onInputChange(value: string): void {
    this.searchQuery.set(value);
    this.showDropdown.set(value.trim().length > 0);
    this.searchInput$.next(value);
  }

  onFocus(): void {
    this.isExpanded.set(true);
    if (this.searchQuery().trim().length > 0) {
      this.showDropdown.set(true);
    }
  }

  /** User clicked a local navigation suggestion */
  selectSuggestion(item: DashboardSearchItem): void {
    this.searchQuery.set(item.title);
    this.showDropdown.set(false);

    if (item.componentKey === 'dashboard') {
      this.searchService.clearSearch();
      this.router.navigate([item.route]);
      return;
    }

    this.searchService.setSearchItem(item);
    this.router.navigate([item.route]);
  }

  /** User clicked a quote from API results → navigate to quote detail */
  selectApiQuote(quote: GlobalSearchQuoteResult): void {
    this.showDropdown.set(false);
    this.searchQuery.set(quote.project_name);
    this.router.navigate(['/quotes', quote.id]);
  }

  /** User clicked a user from API results → navigate to users page with pre-filled search */
  selectApiUser(user: GlobalSearchUserResult): void {
    this.showDropdown.set(false);
    this.searchQuery.set(user.username);
    this.router.navigate(['/users'], {
      queryParams: { search: user.username },
    });
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.showDropdown.set(false);
    this.isExpanded.set(false);
    this.apiQuotes.set([]);
    this.apiUsers.set([]);
    this.searchService.clearSearch();
  }

  private closeDropdown(): void {
    this.showDropdown.set(false);
    if (!this.searchQuery().trim()) {
      this.isExpanded.set(false);
    }
  }
}
